import { useState, useEffect, useMemo, Fragment } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import '../ResultsHub.css';
import usePhotoAlbums, { findAlbum } from './site/usePhotoAlbums';

const API_KEY = import.meta.env.VITE_API_KEY || '';
const CHAMP_FIRST_YEAR = 2022;
const CURRENT_YEAR = new Date().getFullYear();
// New-style (times-driven) /results and /championship links only apply from
// this season on — matches TIMES_DRIVEN_HUB_FIRST_YEAR in results_routes.py.
// 2022-2026 are Jasper seasons: their rounds and championships always link
// to a Jasper report, the archived PDF once one exists, otherwise the live
// Jasper URL the backend supplies (`url`, see LAST_JASPER_YEAR there).
const NEW_STYLE_FIRST_YEAR = 2027;

// Remembers a visitor's "hide video" choice for the hero banner.
const HIDE_VIDEO_COOKIE = 'rh_hide_video';
function readHideVideo() {
  return document.cookie.split('; ').includes(`${HIDE_VIDEO_COOKIE}=1`);
}
function saveHideVideo(hide) {
  document.cookie = hide
    ? `${HIDE_VIDEO_COOKIE}=1;max-age=${365 * 24 * 60 * 60};path=/;SameSite=Lax`
    : `${HIDE_VIDEO_COOKIE}=;max-age=0;path=/;SameSite=Lax`;
}

const CHAMP_OPTIONS = [
  { key: 'TT',   label: 'Top Ten'      },
  { key: 'open', label: 'Open'         },
  { key: 'FE',   label: 'Forgotten Era' },
  { key: 'VC',   label: 'Vin/Classic'  },
  { key: 'RL',   label: 'Road Legal'   },
];

// Jasper seasons (before NEW_STYLE_FIRST_YEAR) show the Jasper report
// instead of the live-computed /championship page — requested 2026-09-18 so
// a settled season's "official" championship result doesn't keep
// moving/reads as authoritative-but-maybe-stale, and extended to 2026 itself
// on 2026-10-07. Maps each class button to the matching results_archive
// `category` value a jasper-sourced row was archived under (see
// scrape_jasper_reports.py).
const JASPER_CATEGORY_BY_CHAMP = {
  open: 'Open Class Ranking',
  FE:   'Forgotten Era Overall',
  VC:   'Vintage Classic',
  RL:   'Road Legal',
  TT:   'Top ten',
};

// The frozen standings doc a class button should open instead of the live
// /championship page, if any: the Jasper report for a Jasper season, or the
// Results Admin freeze (source 'season-close', category = the class key)
// for a 2027+ season.
function frozenChampDoc(season, champKey) {
  return season.year < NEW_STYLE_FIRST_YEAR
    ? season.archive_standings.find(a => a.source === 'jasper' && a.category === JASPER_CATEGORY_BY_CHAMP[champKey])
    : season.archive_standings.find(a => a.source === 'season-close' && a.category === champKey);
}

// Lightweight query-syntax parser for the client-side metadata match, mirroring
// (not reimplementing — this stays deliberately simple) the same syntax
// Postgres's websearch_to_tsquery already gives the document-content search
// for free: "quoted phrases" (exact substring, no per-word splitting),
// OR between two terms (case-insensitive, standalone word), and a leading
// - to exclude a term/phrase. Anything else is a required (AND) term.
function parseSearchQuery(raw) {
  const tokens = raw.match(/-?"[^"]*"|-?\S+/g) || [];
  const notTerms = [];
  const groups = [[]];
  for (const tok of tokens) {
    const negate = tok.startsWith('-');
    let t = negate ? tok.slice(1) : tok;
    const isPhrase = t.startsWith('"') && t.endsWith('"') && t.length > 1;
    t = (isPhrase ? t.slice(1, -1) : t).trim().toLowerCase();
    if (!t) continue;
    if (negate) { notTerms.push(t); continue; }
    if (!isPhrase && t === 'or') { groups.push([]); continue; }
    groups[groups.length - 1].push(t);
  }
  return { groups: groups.filter(g => g.length), notTerms };
}

function matchesQuery(hay, parsed) {
  const h = hay.toLowerCase();
  if (parsed.notTerms.some(t => h.includes(t))) return false;
  if (parsed.groups.length === 0) return true;
  return parsed.groups.some(group => group.every(t => h.includes(t)));
}

function fmtDate(iso) {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

// Short chip text for one of an event's (possibly several) archived docs —
// e.g. "Margam Park 11 Apr 2010 — ALL" -> "ALL". Falls back to a generic
// name when a doc has no " — sublabel" suffix of its own (the common case
// when there's only one doc for the event, or it's the "main" doc among
// several undifferentiated uploads).
// Archived docs are served by id; live Jasper links arrive as a full `url`.
function docHref(doc) {
  return doc.url || `/api/results/archive/${doc.id}`;
}

// Shorter button text for long standings labels (search still matches
// the full label).
const STANDINGS_BUTTON_LABELS = {
  'Forgotten Era Sub-classes': 'FE Subclasses',
};

// Archived labels mostly carry the season's year ("CLASSIC CHAMPIONSHIP
// 1991", "1991: fixtures, records and the Sprinter"), which the season
// heading already shows, so a year at the start or end is dropped. Ranges
// ("2010-2019") and decades ("The 1990s") are kept.
function standingButtonLabel(a, year) {
  const label = STANDINGS_BUTTON_LABELS[a.category] || a.label;
  const short = label
    .replace(new RegExp(`^${year}:?\\s+`), '')
    .replace(new RegExp(`,?\\s+${year}$`), '');
  return short ? short.charAt(0).toUpperCase() + short.slice(1) : label;
}

// Season documents that aren't championship tables: Jamie's archive (yearly
// notes, newsletters, press cuttings) and ones added in Web Admin with no class.
function isFromTheArchive(a) {
  return a.source === 'jamie-archive' || (a.source === 'web-admin' && !a.category);
}

function standingHref(a) {
  return a.url || `/api/results/archive/${a.archive_id}`;
}

// Past seasons prefer the frozen archive doc over the live query once one
// exists — only fall back to live if no archive was found for that event
// yet. Jasper seasons never go live at all (the backend always supplies a
// Jasper link for them). Once most past events have an archive, "live"
// becomes the exception worth flagging, not "archived" — requested
// 2026-09-18.
function canQueryLive(ev, season) {
  if (!ev.has_times || !ev.date) return false;
  if (ev.archive_docs.length === 0) return true;
  return season.year === CURRENT_YEAR && season.year >= NEW_STYLE_FIRST_YEAR;
}

// A round's document buttons: the part of each label after " — " (the
// backend names open-class and FE results "Results" and "FE"), numbered
// where two would read the same ("Results", "Results 2"). Jamie's archive
// labels say what the scans are ("— scans: results, press"); the button
// only needs "Results, press".
function docChipLabels(docs) {
  const marker = ' — ';
  const names = docs.map((doc, i) => {
    const at = doc.label.lastIndexOf(marker);
    if (at === -1) return i === 0 ? 'Results' : 'More';
    const name = doc.label.slice(at + marker.length).replace(/^scans: /, '');
    return name.charAt(0).toUpperCase() + name.slice(1);
  });
  const seen = {};
  return names.map(name => {
    seen[name] = (seen[name] || 0) + 1;
    return names.filter(n => n === name).length > 1 ? `${name}${seen[name] > 1 ? ` ${seen[name]}` : ''}` : name;
  });
}

const PhotoIcon = () => (
  <svg className="rh-media-icon" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M9 4 7.2 6H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-3.2L15 4H9Zm3 4.5a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9Zm0 2a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5Z" />
  </svg>
);

const VideoIcon = () => (
  <svg className="rh-media-icon" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M8 5v14l11-7L8 5Z" />
  </svg>
);

// Legacy archive files are named "<seq> <hill> <date>.pdf" (the season's
// own running order, e.g. "10 Prescott 3rd June 1990.pdf") — the leading
// sequence number is only there for sort order, strip it for display.
function legacyDocLabel(filename) {
  return filename.replace(/^\d+\s+/, '').replace(/\.pdf$/i, '');
}

// One matched round's link(s) inside the search modal — reuses the same
// live-vs-archive, single-vs-multi-doc logic as the inline Rounds grid
// (see the main render below) rather than a simplified subset, so a search
// result is never missing a document the ordinary accordion view would show.
function RoundResultLinks({ ev, season, onNavigate }) {
  const live = canQueryLive(ev, season);
  return (
    <span className="rh-event-docs">
      {ev.archive_docs.map((doc, i) => (
        <a
          key={doc.id ?? doc.url}
          href={docHref(doc)}
          target="_blank"
          rel="noopener noreferrer"
          className="rh-event-doc-link"
        >
          {docChipLabels(ev.archive_docs)[i]}
        </a>
      ))}
      {live && (
        <Link
          to={`/results?hill=${encodeURIComponent(ev.hill)}&date=${ev.date}`}
          className="rh-event-doc-link"
          onClick={onNavigate}
        >
          Live
        </Link>
      )}
    </span>
  );
}

// Max matches to actually render in the search modal — the full dataset
// (all seasons' rounds + standings + legacy docs) is a few thousand items,
// and a broad query (e.g. a single common letter) could match most of it.
const SEARCH_RESULT_CAP = 200;
const DOC_SEARCH_IDLE = { status: 'idle', forQuery: '', results: [], total: 0 };
const ROUND_SEARCH_IDLE = { status: 'idle', results: [] };

export default function ResultsHub() {
  const [hideVideo, setHideVideo] = useState(readHideVideo);
  function toggleVideo() {
    saveHideVideo(!hideVideo);
    setHideVideo(!hideVideo);
  }
  const [seasons, setSeasons]     = useState([]);
  const photoAlbums = usePhotoAlbums();
  const [openYears, setOpenYears] = useState(new Set());
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  // Synced to the URL (?q=), not plain useState — a search-result click that
  // navigates same-tab (a live event's Link, see below) unmounts this
  // component; without the URL as the source of truth, hitting Back landed
  // on a blank search box with no way to resume/modify the same search.
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  function setQuery(v) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (v) next.set('q', v); else next.delete('q');
      return next;
    }, { replace: true }); // replace, not push — every keystroke would otherwise bloat browser history
  }
  const [sortOrder, setSortOrder] = useState('asc');

  useEffect(() => {
    fetch('/api/results/index', { headers: { 'X-API-Key': API_KEY } })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(data => {
        setSeasons(data);
        if (data.length > 0) setOpenYears(new Set([data[0].year]));
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    // closeSearch (a function declaration, hoisted) just dismisses the
    // dropdown now — it no longer clears the search term, so Escape doesn't
    // wipe what you've typed either.
    function onKey(e) { if (e.key === 'Escape') closeSearch(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  function toggleYear(year) {
    setOpenYears(prev => {
      const next = new Set(prev);
      next.has(year) ? next.delete(year) : next.add(year);
      return next;
    });
  }

  // Client-side, over data already fully loaded above — searches round
  // hill/event names, championship-standings doc labels, AND legacy archive
  // filenames (all three carry their own year already, so "2017" or "FE"
  // work as search terms too, not just hill names). Same query syntax
  // (quotes/OR/-NOT) as the document-content search below, via
  // parseSearchQuery/matchesQuery.
  const searchResults = useMemo(() => {
    const q = query.trim();
    if (q.length < 2) return { groups: [], total: 0 };
    const parsed = parseSearchQuery(q);
    const flat = [];
    for (const season of seasons) {
      for (const ev of season.events) {
        const hay = `${ev.hill} ${ev.event_name || ''} ${season.year}`;
        if (matchesQuery(hay, parsed)) {
          flat.push({ year: season.year, kind: 'round', key: `r-${season.year}-${ev.hill}-${ev.date || ev.event_name}`, ev });
        }
      }
      for (const a of (season.archive_standings || [])) {
        if (matchesQuery(`${a.label} ${season.year}`, parsed)) {
          flat.push({ year: season.year, kind: 'link', key: `s-${a.archive_id ?? a.url}`, label: a.label, href: standingHref(a) });
        }
      }
      for (const filename of (season.legacy_archive_docs || [])) {
        const label = legacyDocLabel(filename);
        if (matchesQuery(`${label} ${season.year}`, parsed)) {
          flat.push({ year: season.year, kind: 'link', key: `l-${season.year}-${filename}`, label, href: `/api/results/legacy-archive/${season.year}/${encodeURIComponent(filename)}` });
        }
      }
    }
    const groups = [];
    for (const item of flat.slice(0, SEARCH_RESULT_CAP)) {
      const last = groups[groups.length - 1];
      if (last && last.year === item.year) last.items.push(item);
      else groups.push({ year: item.year, items: [item] });
    }
    return { groups, total: flat.length };
  }, [query, seasons]);

  // Dismissing the dropdown (overlay click, ×, Escape, clicking a result)
  // is a separate concern from clearing the search term — it used to call
  // setQuery(''), which also wiped the input since searchOpen derives from
  // query itself, so clicking away destroyed whatever you'd typed. `dismissed`
  // is local (not URL-synced) so it resets to false on every fresh mount.
  //
  // Also set synchronously on every keystroke (the input's onChange), so
  // the dropdown stays hidden for the whole time you're actively editing —
  // no matter how long that takes. Earlier versions tried clearing this via
  // a debounce timer instead (400ms, then 800ms, then 1500ms — still not
  // enough: on mobile, cursoring to a position mid-string, selecting text,
  // etc. can easily take longer than any fixed delay short enough to still
  // feel responsive for a fresh search). Replaced with explicit triggers
  // instead of a delay: runDocSearch (below) is the only thing that clears
  // it, called from Enter and blur (see the input's handlers) — not time.
  const [dismissed, setDismissed] = useState(false);
  const searchOpen = query.trim().length >= 2 && !dismissed;
  function closeSearch() { setDismissed(true); }

  // Search-inside-documents — explicitly triggered (Enter / blur on the
  // input, or the sort-toggle button), not per-keystroke or on a timer; see
  // the `dismissed` comment above for why. `total` is the REAL match count
  // from the backend (a window-function count, not just results.length) —
  // the API always caps `results` at 50, so without this there's no way to
  // tell "these are all of them" from "there are hundreds more" (came up
  // 2026-09-20 comparing AND vs OR search result sizes).
  const [docSearch, setDocSearch] = useState(DOC_SEARCH_IDLE);
  // Rounds matched straight from the results data (rider/passenger/machine,
  // 2022+ — see search_rounds() in results_routes.py), so a round is
  // findable before its PDF is archived and text-indexed. Fired alongside
  // the document search, by the same explicit triggers.
  const [roundSearch, setRoundSearch] = useState(ROUND_SEARCH_IDLE);
  function runDocSearch(rawQuery, order) {
    const q = rawQuery.trim();
    setDismissed(false);
    if (q.length < 2) {
      setDocSearch(DOC_SEARCH_IDLE);
      setRoundSearch(ROUND_SEARCH_IDLE);
      return;
    }
    setRoundSearch(prev => ({ ...prev, status: 'loading' }));
    fetch(`/api/results/search-rounds?q=${encodeURIComponent(q)}&order=${order}`, { headers: { 'X-API-Key': API_KEY } })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(data => setRoundSearch({ status: 'done', results: data.results }))
      .catch(() => setRoundSearch({ status: 'error', results: [] }));
    setDocSearch(prev => ({ ...prev, status: 'loading' }));
    fetch(`/api/results/search-documents?q=${encodeURIComponent(q)}&order=${order}`, { headers: { 'X-API-Key': API_KEY } })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(data => setDocSearch({ status: 'done', forQuery: q, results: data.results, total: data.total }))
      .catch(() => setDocSearch({ status: 'error', forQuery: q, results: [], total: 0 }));
  }

  // Restores results on mount for a search already in the URL (e.g. Back
  // navigation to a previous search, or a shared/bookmarked link) — those
  // shouldn't need an extra Enter/blur just to reappear.
  useEffect(() => {
    if (query.trim().length >= 2) runDocSearch(query, sortOrder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetches the next page of document matches (offset = however many are
  // already loaded) and appends rather than replaces — mergedGroups below
  // just re-runs over the longer results array, so newly-loaded matches
  // slot into their correct year group automatically.
  function loadMoreDocResults() {
    const q = docSearch.forQuery;
    const offset = docSearch.results.length;
    setDocSearch(prev => ({ ...prev, status: 'loading-more' }));
    fetch(`/api/results/search-documents?q=${encodeURIComponent(q)}&offset=${offset}&order=${sortOrder}`, { headers: { 'X-API-Key': API_KEY } })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(data => setDocSearch(prev => ({ status: 'done', forQuery: q, results: [...prev.results, ...data.results], total: data.total })))
      .catch(() => setDocSearch(prev => ({ ...prev, status: 'done' })));
  }

  // Merges the instant metadata matches with the (explicitly-triggered,
  // slower) document-content matches into ONE per-year list — a title match and a
  // content match for the same document become one entry (with a snippet
  // attached) rather than two, and a content-only match gets its own new
  // entry in the right year group.
  const mergedGroups = useMemo(() => {
    const groupsByYear = new Map();
    for (const g of searchResults.groups) {
      groupsByYear.set(g.year, { year: g.year, items: g.items.map(it => ({ ...it })) });
    }
    // Results-data round matches go in first: a round already listed by the
    // metadata match just gains the snippet, otherwise it's added as its own
    // round entry. Either way its docs count as visible below, so a PDF hit
    // for the same round doesn't list it a second time.
    if (roundSearch.status === 'done') {
      const evIndex = new Map();
      for (const season of seasons) {
        for (const ev of season.events) {
          if (ev.date) evIndex.set(`${season.year}|${ev.hill}|${ev.date}`, ev);
        }
      }
      const roundItems = new Map();
      for (const g of groupsByYear.values()) {
        for (const item of g.items) if (item.kind === 'round') roundItems.set(item.key, item);
      }
      for (const hit of roundSearch.results) {
        const ev = evIndex.get(`${hit.year}|${hit.hill}|${hit.event_date}`);
        if (!ev) continue;
        const key = `r-${hit.year}-${ev.hill}-${ev.date}`;
        const existing = roundItems.get(key);
        if (existing) {
          existing.snippet = hit.snippet;
          continue;
        }
        const item = { year: hit.year, kind: 'round', key, ev, snippet: hit.snippet };
        let g = groupsByYear.get(hit.year);
        if (!g) { g = { year: hit.year, items: [] }; groupsByYear.set(hit.year, g); }
        g.items.push(item);
        roundItems.set(key, item);
      }
    }
    const linkIndex = new Map();
    // Only rounds actually VISIBLE in this search's own metadata matches
    // (i.e. the round's own hill/event name matched the query) already
    // offer a way to reach their archived doc(s) via RoundResultLinks —
    // scoped to this search, not "every round in the whole 90-year
    // dataset", otherwise a content-only match whose round never matched
    // the query gets silently swallowed (found and fixed 2026-09-20: this
    // was hiding nearly every event-document content hit, since almost
    // every event document belongs to *some* round somewhere).
    const visibleRoundArchiveIds = new Set();
    for (const g of groupsByYear.values()) {
      for (const item of g.items) {
        if (item.kind === 'link') linkIndex.set(item.key, item);
        if (item.kind === 'round') {
          for (const doc of item.ev.archive_docs || []) visibleRoundArchiveIds.add(doc.id);
        }
      }
    }
    if (docSearch.status === 'done' || docSearch.status === 'loading-more') {
      for (const doc of docSearch.results) {
        const key = doc.archive_id ? `s-${doc.archive_id}` : `l-${doc.legacy_year}-${doc.legacy_filename}`;
        const existing = linkIndex.get(key);
        if (existing) {
          existing.snippet = doc.snippet;
          continue;
        }
        if (doc.archive_id && visibleRoundArchiveIds.has(doc.archive_id)) continue;
        // Trust the backend's own sort_year (now returned as `year`) rather
        // than re-deriving it here — the old fallback (event_date, else
        // legacy_year) produced `undefined` for every season-standings/
        // championship-table match: those are archive_id-backed, not
        // Legacy Archive filesystem docs, so legacy_year is null for them
        // too, and they have no event_date either. A single undefined year
        // breaks the whole sort below (year - undefined = NaN), not just
        // that group's own position — found 2026-09-21 via a real search
        // ("Simon Wilson" Curborough) putting the earliest real event dead
        // last instead of merely behind the undated championship tables.
        const year = doc.year;
        const newItem = {
          kind: 'link',
          key: `d-${key}`,
          label: docResultLabel(doc),
          href: docResultLink(doc),
          context: docResultContext(doc),
          snippet: doc.snippet,
        };
        let g = groupsByYear.get(year);
        if (!g) { g = { year, items: [] }; groupsByYear.set(year, g); }
        g.items.push(newItem);
        linkIndex.set(key, newItem);
      }
    }
    const groups = [...groupsByYear.values()].sort((a, b) => sortOrder === 'desc' ? b.year - a.year : a.year - b.year);
    const total = groups.reduce((n, g) => n + g.items.length, 0);
    return { groups, total };
  }, [searchResults, docSearch.status, docSearch.results, roundSearch, seasons, sortOrder]);

  // ts_headline snippets come back with \x01/\x02 markers around each
  // match instead of raw <b> tags (see search_documents() in
  // results_routes.py) — split and render as plain text/bold spans here
  // rather than ever treating extracted document text as trusted HTML.
  function renderSnippet(snippet) {
    // eslint-disable-next-line no-control-regex -- deliberate marker chars, see comment above
    const parts = snippet.split(/[\x01\x02]/);
    return parts.map((part, i) =>
      i % 2 === 1 ? <b key={i}>{part}</b> : <span key={i}>{part}</span>
    );
  }

  function docResultLink(doc) {
    return doc.archive_id
      ? `/api/results/archive/${doc.archive_id}`
      : `/api/results/legacy-archive/${doc.legacy_year}/${encodeURIComponent(doc.legacy_filename)}`;
  }

  function docResultLabel(doc) {
    return doc.label || legacyDocLabel(doc.legacy_filename);
  }

  // Document search results span ~90 years, unlike the single-season Rounds
  // grid fmtDate() was written for — always include the year here, since
  // "15 Jun" alone (fmtDate's normal output) is ambiguous across the whole
  // archive.
  function docResultContext(doc) {
    if (doc.event_date) {
      const year = doc.event_date.slice(0, 4);
      return doc.hill ? `${doc.hill}, ${fmtDate(doc.event_date)} ${year}` : `${fmtDate(doc.event_date)} ${year}`;
    }
    if (doc.legacy_year) return String(doc.legacy_year);
    return '';
  }

  return (
    <div className="rh-page">

      <div className={`rh-hero${hideVideo ? ' rh-hero-no-video' : ''}`}>
        {!hideVideo && (
          <video
            className="rh-header-video"
            autoPlay
            muted
            loop
            playsInline
          >
            <source src="/championship/header_video.mp4" type="video/mp4" />
          </video>
        )}
        <h1 className="rh-hero-title">NHCA Results</h1>
        <button type="button" className="rh-video-toggle" onClick={toggleVideo}>
          {hideVideo ? 'Show video' : 'Hide video'}
        </button>
      </div>

      <div className="rh-site-links">
        <Link to="/" className="rh-report-btn" target="_blank" rel="noopener noreferrer">
          Rider Stats
        </Link>
        <a href="https://live.nhca.co.uk/" className="rh-report-btn" target="_blank" rel="noopener noreferrer">
          Live Timing
        </a>
        <Link to="/records?Class=open" className="rh-report-btn" target="_blank" rel="noopener noreferrer">
          Records
        </Link>
        <Link to="/target-times" className="rh-report-btn" target="_blank" rel="noopener noreferrer">
          Target Times
        </Link>
      </div>

      <div className="rh-search-row">
        <input
          type="text"
          className="rh-search-input"
          placeholder="Search hills, events, years, legacy documents…"
          value={query}
          onChange={e => { setQuery(e.target.value); setDismissed(true); }}
          onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }} // triggers the onBlur handler below — one path, not two
          onBlur={() => runDocSearch(query, sortOrder)}
          aria-label="Search results"
        />
      </div>

      {searchOpen && (
        <div className="rh-search-overlay" onClick={closeSearch}>
          <div className="rh-search-modal" onClick={e => e.stopPropagation()}>
            <div className="rh-search-modal-header">
              <span>
                {/* mergedGroups.total only counts what's actually loaded — when either
                    match source is truncated (more document matches than this page
                    fetched, or more metadata matches than SEARCH_RESULT_CAP), that
                    count understates the real total, so say "More than N" rather than
                    a bare number a reader could mistake for the complete count. */}
                {(searchResults.total > SEARCH_RESULT_CAP || docSearch.total > docSearch.results.length) ? 'More than ' : ''}
                {mergedGroups.total} result{mergedGroups.total === 1 ? '' : 's'} for “{query.trim()}”
                {searchResults.total > SEARCH_RESULT_CAP && ` — showing first ${SEARCH_RESULT_CAP}`}
                {(docSearch.status === 'loading' || roundSearch.status === 'loading') && ' · searching results and document text…'}
                {docSearch.status === 'error' && ' · document text search failed'}
                {roundSearch.status === 'error' && ' · results search failed'}
                {(docSearch.status === 'done' || docSearch.status === 'loading-more') && docSearch.total > docSearch.results.length &&
                  ` · ${docSearch.total} document matches (showing first ${docSearch.results.length})`}
              </span>
              <button
                type="button"
                className="rh-search-sort-toggle"
                onClick={() => {
                  const next = sortOrder === 'asc' ? 'desc' : 'asc';
                  setSortOrder(next);
                  runDocSearch(query, next); // explicit action, same as Enter/blur — re-fetches immediately in the new order
                }}
                title="Toggle sort order"
              >
                {sortOrder === 'asc' ? 'Oldest first ↑' : 'Newest first ↓'}
              </button>
              <button className="rh-search-close" onClick={closeSearch} aria-label="Close search results">×</button>
            </div>
            <div className="rh-search-modal-body">
              {mergedGroups.total === 0 && docSearch.status !== 'loading' && roundSearch.status !== 'loading' && (
                <div className="rh-search-empty">No matches.</div>
              )}
              {mergedGroups.groups.map(group => {
                const season = seasons.find(s => s.year === group.year);
                return (
                  <div key={group.year} className="rh-search-year-group">
                    <div className="rh-search-year-label">{group.year}</div>
                    {group.items.map(item => item.kind === 'round' ? (
                      <div key={item.key} className="rh-search-result-row">
                        <span className="rh-search-result-label">
                          {item.ev.date ? `${fmtDate(item.ev.date)} — ` : ''}{item.ev.hill}
                          {item.ev.event_name ? ` (${item.ev.event_name})` : ''}
                        </span>
                        <RoundResultLinks ev={item.ev} season={season} onNavigate={closeSearch} />
                        {item.snippet && <span className="rh-doc-snippet rh-round-snippet">{renderSnippet(item.snippet)}</span>}
                      </div>
                    ) : (
                      <a
                        key={item.key}
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`rh-search-result-row rh-search-result-link${item.snippet ? ' rh-doc-result' : ''}`}
                        onClick={closeSearch}
                      >
                        <span className="rh-search-result-label">
                          {item.label}
                          {item.context && ` — ${item.context}`}
                        </span>
                        {item.snippet && <span className="rh-doc-snippet">{renderSnippet(item.snippet)}</span>}
                      </a>
                    ))}
                  </div>
                );
              })}
              {(docSearch.status === 'done' || docSearch.status === 'loading-more') && docSearch.total > docSearch.results.length && (
                <button
                  className="rh-doc-search-btn"
                  onClick={loadMoreDocResults}
                  disabled={docSearch.status === 'loading-more'}
                >
                  {docSearch.status === 'loading-more'
                    ? 'Loading…'
                    : `Load ${Math.min(50, docSearch.total - docSearch.results.length)} more document matches (${docSearch.total - docSearch.results.length} remaining)`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {loading && <div className="rh-loading">Loading seasons…</div>}
      {error   && <div className="rh-error">Failed to load results: {error}</div>}

      {seasons.map(season => {
        const isOpen = openYears.has(season.year);
        return (
          <div key={season.year} className="rh-season">
            <button
              className="rh-season-header"
              onClick={() => toggleYear(season.year)}
              aria-expanded={isOpen}
            >
              <span className="rh-season-year">
                {season.year ? `${season.year} Season` : 'Unknown year'}
                <span className="rh-season-count">
                  {season.events.length > 0
                    ? `${season.events.length} ${season.events.length === 1 ? 'round' : 'rounds'}`
                    : (() => {
                        const docs = (season.legacy_archive_docs?.length || 0) + (season.archive_standings?.length || 0);
                        return docs ? `${docs} archive document${docs === 1 ? '' : 's'}` : '0 rounds';
                      })()}
                </span>
              </span>
              <span className={`rh-chevron${isOpen ? ' open' : ''}`}>▼</span>
            </button>

            {isOpen && (
              <div className="rh-season-body">

                {(() => {
                  // Class buttons open the frozen standings where there is
                  // one (frozenChampDoc), otherwise the live page. Any other
                  // standings docs for the season join the same row: FE
                  // Subclasses straight after Forgotten Era, the rest at the
                  // end. Compared by object, not archive_id — a live Jasper
                  // link has no archive_id.
                  const classes = season.year >= CHAMP_FIRST_YEAR ? CHAMP_OPTIONS : [];
                  const promoted = new Set(classes.map(c => frozenChampDoc(season, c.key)).filter(Boolean));
                  // Jamie's archive season documents (yearly notes, newsletters,
                  // press cuttings) get their own row below, not "Championships".
                  const remaining = (season.archive_standings || [])
                    .filter(a => !promoted.has(a) && !isFromTheArchive(a));
                  const feSubclasses = remaining.find(a => a.category === 'Forgotten Era Sub-classes');
                  const others = remaining.filter(a => a !== feSubclasses);
                  const standingDocButton = a => (
                    <a
                      key={a.archive_id ?? a.url}
                      href={standingHref(a)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rh-champ-btn"
                    >
                      {standingButtonLabel(a, season.year)}
                    </a>
                  );
                  const showWhoCanWin = season.year === CURRENT_YEAR;
                  const fromArchive = (season.archive_standings || []).filter(isFromTheArchive);
                  const archiveRow = fromArchive.length > 0 && (
                    <div className="rh-championships">
                      <div className="rh-section-label">From the archive</div>
                      <div className="rh-champ-row">
                        <div className="rh-champ-buttons">{fromArchive.map(standingDocButton)}</div>
                      </div>
                    </div>
                  );
                  if (!classes.length && !remaining.length && !showWhoCanWin) return archiveRow || null;
                  return (
                  <>
                  <div className="rh-championships">
                    {(classes.length > 0 || remaining.length > 0) && (
                      <div className="rh-section-label">Championships</div>
                    )}
                    <div className="rh-champ-row">
                      <div className="rh-champ-buttons">
                        {classes.map(c => {
                          const frozenDoc = frozenChampDoc(season, c.key);
                          const button = frozenDoc ? (
                            <a
                              href={standingHref(frozenDoc)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="rh-champ-btn"
                            >
                              {c.label}
                            </a>
                          ) : (
                            <Link
                              to={`/championship?season=${season.year}&class=${c.key}`}
                              className="rh-champ-btn"
                              {...(season.year === CURRENT_YEAR ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                            >
                              {c.label}
                            </Link>
                          );
                          return (
                            <Fragment key={c.key}>
                              {button}
                              {c.key === 'FE' && feSubclasses && standingDocButton(feSubclasses)}
                            </Fragment>
                          );
                        })}
                        {!classes.length && feSubclasses && standingDocButton(feSubclasses)}
                        {others.map(standingDocButton)}
                      </div>
                      {showWhoCanWin && (
                        <div className="rh-report-buttons">
                          <Link to="/score-ranges?champ=open" className="rh-report-btn" target="_blank" rel="noopener noreferrer">
                            Who Can Win?
                          </Link>
                        </div>
                      )}
                    </div>
                  </div>
                  {archiveRow}
                  </>
                  );
                })()}

                {season.legacy_archive_docs && season.legacy_archive_docs.length > 0 && (
                  <>
                  <div className="rh-section-label">
                    Legacy Archive — {season.legacy_archive_docs.length} document{season.legacy_archive_docs.length === 1 ? '' : 's'}
                  </div>
                  <div className="rh-events-grid">
                    {season.legacy_archive_docs.map(filename => (
                      <a
                        key={filename}
                        href={`/api/results/legacy-archive/${season.year}/${encodeURIComponent(filename)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rh-event-row"
                      >
                        <span className="rh-legacy-doc-label">{legacyDocLabel(filename)}</span>
                      </a>
                    ))}
                  </div>
                  </>
                )}

                {season.events.length > 0 && (
                <>
                <div className="rh-section-label">Rounds</div>
                <div className="rh-events-grid">
                  {season.events.map((ev) => {
                    const live = canQueryLive(ev, season);
                    // date+hill is already the backend's own dedup key (see
                    // event_index/archive_event_index in results_routes.py),
                    // so it's always unique per row when dated — unlike
                    // archive_docs[0]?.id, which collides for a 2-day event's
                    // repeated day-rows (both share the same underlying
                    // document). Undated rows aren't deduped by the backend
                    // at all (can't safely tell two apart without a date),
                    // so fall back to the doc-based key there instead.
                    const key = ev.date
                      ? `${ev.date}-${ev.hill}`
                      : (ev.calendar_id ?? ev.archive_docs[0]?.id ?? ev.archive_docs[0]?.url ?? `${ev.hill}-${ev.event_name}`);
                    const info = (
                      <>
                        <span className="rh-event-date">{ev.date ? fmtDate(ev.date) : ''}</span>
                        {/* The name mostly repeats the hill and date ("Hartland
                            Quay 20th March"), so only undated rows show it, in
                            place of the hill: it's what tells them apart
                            ("Packington 1968 (meeting 6)"), shown without the
                            season's year ("Packington (meeting 6)"). The part
                            after " — " is on the buttons. */}
                        {ev.event_name && !ev.date ? (
                          <span className="rh-event-hill rh-event-hill--undated">
                            {ev.event_name.split(' — ')[0].replace(` ${season.year}`, '')}
                          </span>
                        ) : (
                          <span className="rh-event-hill" title={ev.hill}>{ev.hill}</span>
                        )}
                        {live && (
                          <span className="rh-event-name">(live)</span>
                        )}
                      </>
                    );

                    const album = findAlbum(photoAlbums.events, ev.date, ev.hill, season.year,
                      season.events.some(o => o !== ev && o.date === ev.date));
                    const videos = ev.videos || [];
                    // Every round is the same: date and hill, then the
                    // buttons, laid out right to left (.rh-event-docs) so
                    // Results is always at the right edge and lines up down
                    // the column: the documents, Live while its results are
                    // live, then photos and videos.
                    const docLabels = docChipLabels(ev.archive_docs);
                    return (
                      <div key={key} className="rh-event-row rh-event-row-multi">
                        {info}
                        <span className="rh-event-docs">
                          {ev.archive_docs.map((doc, i) => (
                            <a
                              key={doc.id ?? doc.url}
                              href={docHref(doc)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="rh-event-doc-link"
                            >
                              {docLabels[i]}
                            </a>
                          ))}
                          {live && (
                            <Link
                              to={`/results?hill=${encodeURIComponent(ev.hill)}&date=${ev.date}`}
                              className="rh-event-doc-link"
                            >
                              Live
                            </Link>
                          )}
                          {(album || videos.length > 0) && (
                            <span className="rh-event-media">
                              {album && (
                                <a
                                  href={album.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="rh-media-link rh-media-link--photos"
                                  title={`${album.count} photos`}
                                >
                                  <PhotoIcon />Photos
                                </a>
                              )}
                              {/* YouTube videos of the event, added in Web Admin (Event videos) */}
                              {videos.map((video, i) => (
                                <a
                                  key={video.url}
                                  href={video.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="rh-media-link rh-media-link--video"
                                  title={video.title}
                                >
                                  <VideoIcon />{videos.length > 1 ? `Video ${i + 1}` : 'Video'}
                                </a>
                              ))}
                            </span>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
                </>
                )}

              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
